// The owner's numbers (D-163): the customers by stage as they stand, what moved in a period, how fast new enquiries
// were called, where customers came from and how far each source's got, why customers were lost, and each person's
// customers and follow-up discipline, each number opening the customers or follow-ups behind it. A telecaller sees the
// same for her own customers.
import { useSearchParams } from "react-router";
import { type Insights, onTimeShare, pct, waitWords } from "../lib/crm";
import { fmtDate, fmtNumber, isManagerRole } from "../lib/format";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, Loading, Page, Stat, useLoad } from "../app/ui";

const PERIODS = [7, 30, 90] as const;

export function Pipeline() {
  const { api, member, tz } = useMember();
  const [params, setParams] = useSearchParams();
  const days = PERIODS.find((d) => String(d) === params.get("days")) ?? 30;
  // The last 7, 30 or 90 days, up to now.
  const { data, error, loading } = useLoad(
    () => {
      const to = new Date();
      const from = new Date(to.getTime() - days * 86_400_000);
      return api.insights(from.toISOString(), to.toISOString());
    },
    [api, days],
    true,
  );
  const manager = isManagerRole(member.role);

  return (
    <Page
      title={manager ? "Pipeline" : "My pipeline"}
      sub={data ? `${fmtDate(data.from, tz)} to ${fmtDate(data.to, tz)}` : undefined}
      actions={
        <div className="segmented" role="group" aria-label="Period">
          {PERIODS.map((d) => (
            <button
              key={d}
              className={d === days ? "on" : ""}
              onClick={() => setParams({ days: String(d) })}
            >
              {d} days
            </button>
          ))}
        </div>
      }
    >
      <ErrorNote error={error} />
      {loading && !data && <Loading />}
      {data && <Numbers data={data} manager={manager} days={days} />}
    </Page>
  );
}

function Numbers({ data, manager, days }: { data: Insights; manager: boolean; days: number }) {
  const open = data.pipeline.filter((s) => s.kind === "open");
  const closed = data.pipeline.filter((s) => s.kind !== "open");
  const maxOpen = Math.max(1, ...open.map((s) => s.count));
  const openTotal = open.reduce((a, s) => a + s.count, 0);
  const lostMax = Math.max(1, ...data.lost_reasons.map((r) => r.count));
  const speed = data.speed;
  const m = data.moved;

  return (
    <div className="stack">
      <div className="grid cols-4">
        <Stat
          label="New customers"
          value={fmtNumber(m.new)}
          hint={`in ${days} days`}
          href={`#/leads?added=${days <= 7 ? "week" : "month"}`}
        />
        <Stat label="Called for the first time" value={fmtNumber(m.contacted)} />
        <Stat
          label="Visits and meetings"
          value={fmtNumber(m.visits_booked)}
          hint={`booked; ${fmtNumber(m.visits_done)} happened`}
        />
        <Stat
          label="Won"
          value={fmtNumber(m.won)}
          hint={`${fmtNumber(m.lost)} lost${m.won_value ? ` · ₹${fmtNumber(m.won_value)}` : ""}`}
        />
      </div>

      {manager && (data.unassigned ?? 0) > 0 && (
        <a className="card alert-line" href="#/leads?owner=none&status=open">
          <strong>
            {fmtNumber(data.unassigned)} open {data.unassigned === 1 ? "customer has" : "customers have"} nobody.
          </strong>{" "}
          Choose them on Customers and hand them over.
        </a>
      )}

      <div className="split">
        <Card title={`Open customers by stage · ${fmtNumber(openTotal)}`}>
          {open.length === 0 && <Empty>No stages.</Empty>}
          {open.map((s) => (
            <a key={s.id} className="hbar stage-bar" href={`#/leads?stage=${s.id}`} title={`${s.name}: ${s.count}`}>
              <span>
                {s.name}
                {(s.hot > 0 || s.warm > 0) && (
                  <span className="faint">
                    {` · ${
                      [s.hot > 0 ? `${s.hot} hot` : "", s.warm > 0 ? `${s.warm} warm` : ""].filter(Boolean).join(", ")
                    }`}
                  </span>
                )}
              </span>
              <span className="track">
                <div style={{ width: `${(s.count / maxOpen) * 100}%` }} />
              </span>
              <span className="num">{fmtNumber(s.count)}</span>
            </a>
          ))}
          {closed.length > 0 && (
            <div className="faint closed-line">
              {closed.map((s, i) => (
                <span key={s.id}>
                  {i > 0 && " · "}
                  <a href={`#/leads?stage=${s.id}`}>{s.name} {fmtNumber(s.count)}</a>
                </span>
              ))}
            </div>
          )}
        </Card>

        <Card title="How fast new enquiries were called">
          {speed.leads === 0 ? <Empty>No enquiries from ads, portals or the website in this period.</Empty> : (
            <>
              <div className="speed">
                <div>
                  <div className="speed-value">{waitWords(speed.median_minutes)}</div>
                  <div className="faint">half were called sooner than this</div>
                </div>
                <div>
                  <div className="speed-value">{pct(speed.within_5m, speed.leads)}</div>
                  <div className="faint">within 5 minutes</div>
                </div>
                <div>
                  <div className="speed-value">{pct(speed.within_1h, speed.leads)}</div>
                  <div className="faint">within an hour</div>
                </div>
                <div>
                  <div className={`speed-value${speed.leads - speed.called > 0 ? " overdue" : ""}`}>
                    {fmtNumber(speed.leads - speed.called)}
                  </div>
                  <div className="faint">not called yet</div>
                </div>
              </div>
              <p className="faint" style={{ marginBottom: 0 }}>
                Of {fmtNumber(speed.leads)}{" "}
                enquiries that came in by themselves (ads, portals, the website). The sooner a new enquiry is called,
                the likelier it picks up.
              </p>
            </>
          )}
        </Card>
      </div>

      <Card title="Where customers came from" flush>
        {data.sources.length === 0 && <Empty>No new customers in this period.</Empty>}
        {data.sources.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Source</th>
                  <th className="num">New customers</th>
                  <th className="num">Called</th>
                  <th className="num">Spoke</th>
                  <th className="num">Interested</th>
                  <th className="num">Visit or meeting</th>
                  <th className="num">Won</th>
                </tr>
              </thead>
              <tbody>
                {data.sources.map((s) => (
                  <tr key={`${s.id}-${s.name}`}>
                    <td>
                      {s.id
                        ? <a href={`#/leads?source=${s.id}&added=${days <= 7 ? "week" : "month"}`}>{s.name}</a>
                        : s.name}
                    </td>
                    <td className="num">{fmtNumber(s.leads)}</td>
                    <td className="num">
                      {fmtNumber(s.called)} <span className="faint">{pct(s.called, s.leads)}</span>
                    </td>
                    <td className="num">
                      {fmtNumber(s.connected)} <span className="faint">{pct(s.connected, s.leads)}</span>
                    </td>
                    <td className="num">
                      {fmtNumber(s.interested)} <span className="faint">{pct(s.interested, s.leads)}</span>
                    </td>
                    <td className="num">
                      {fmtNumber(s.visit)} <span className="faint">{pct(s.visit, s.leads)}</span>
                    </td>
                    <td className="num">
                      {fmtNumber(s.won)} <span className="faint">{pct(s.won, s.leads)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="split">
        <Card title="Why customers were lost">
          {data.lost_reasons.length === 0 && <Empty>Nobody was lost in this period.</Empty>}
          {data.lost_reasons.map((r) => (
            <div key={r.reason} className="hbar lost-bar" title={`${r.reason}: ${r.count}`}>
              <span>{r.reason}</span>
              <span className="track">
                <div style={{ width: `${(r.count / lostMax) * 100}%` }} />
              </span>
              <span className="num">{fmtNumber(r.count)}</span>
            </div>
          ))}
        </Card>
        <Card title="What to look at">
          <Advice data={data} manager={manager} />
        </Card>
      </div>

      <Card title={manager ? "Each person" : "Your customers and follow-ups"} flush>
        {data.people.length === 0 && <Empty>Nobody yet.</Empty>}
        {data.people.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th className="num">Open customers</th>
                  <th className="num">Never called</th>
                  <th className="num">No next step</th>
                  <th className="num">Overdue follow-ups</th>
                  <th className="num">Follow-ups done</th>
                  <th className="num">On time</th>
                  <th className="num">Visits booked</th>
                  <th className="num">Won</th>
                </tr>
              </thead>
              <tbody>
                {data.people.map((p) => {
                  const mine = manager ? `owner=${p.id}` : "owner=me";
                  const share = onTimeShare(p.done, p.on_time);
                  return (
                    <tr key={p.id}>
                      <td>{p.name}</td>
                      <td className="num">
                        <a href={`#/leads?${mine}&status=open`}>{fmtNumber(p.open)}</a>
                      </td>
                      <td className={`num${p.untouched > 0 ? " warn-cell" : ""}`}>
                        <a href={`#/leads?${mine}&contact=never&status=open`}>{fmtNumber(p.untouched)}</a>
                      </td>
                      <td className={`num${p.no_next > 0 ? " warn-cell" : ""}`}>
                        <a href={`#/leads?${mine}&follow_up=none`}>{fmtNumber(p.no_next)}</a>
                      </td>
                      <td className={`num${p.overdue > 0 ? " overdue" : ""}`}>
                        <a href={manager ? `#/today?who=${p.id}&due=overdue` : "#/today?due=overdue"}>
                          {fmtNumber(p.overdue)}
                        </a>
                      </td>
                      <td className="num">{fmtNumber(p.done)}</td>
                      <td className="num">{share === null ? <span className="faint">–</span> : `${share}%`}</td>
                      <td className="num">{fmtNumber(p.visits_booked)}</td>
                      <td className="num">{fmtNumber(p.won)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** A few plain sentences on what the numbers say needs doing first. */
function Advice({ data, manager }: { data: Insights; manager: boolean }) {
  const lines: { text: string; href?: string }[] = [];
  const untouched = data.people.reduce((a, p) => a + p.untouched, 0);
  const overdue = data.people.reduce((a, p) => a + p.overdue, 0);
  const noNext = data.people.reduce((a, p) => a + p.no_next, 0);
  if (data.speed.leads > 0 && data.speed.leads - data.speed.called > 0) {
    lines.push({
      text: `${
        fmtNumber(data.speed.leads - data.speed.called)
      } new enquiries from ads or portals have not been called yet.`,
      href: "#/leads?contact=never&status=open&sort=newest",
    });
  }
  if (overdue > 0) {
    lines.push({ text: `${fmtNumber(overdue)} follow-ups are overdue.`, href: "#/today?due=overdue" });
  }
  if (untouched > 0) {
    lines.push({
      text: `${fmtNumber(untouched)} open customers were never called.`,
      href: "#/leads?contact=never&status=open",
    });
  }
  if (noNext > 0) {
    lines.push({
      text: `${fmtNumber(noNext)} customers were spoken to but have no next step planned.`,
      href: "#/leads?follow_up=none",
    });
  }
  if (manager && (data.unassigned ?? 0) > 0) {
    lines.push({
      text: `${fmtNumber(data.unassigned)} open customers have nobody.`,
      href: "#/leads?owner=none&status=open",
    });
  }
  const slow = data.speed.median_minutes;
  if (slow != null && slow > 60) {
    lines.push({ text: `New enquiries wait ${waitWords(slow)} for a first call, half of them longer.` });
  }
  if (lines.length === 0) return <div className="muted">Nothing stands out. Every customer has a next step.</div>;
  return (
    <ul className="advice">
      {lines.map((l) => <li key={l.text}>{l.href ? <a href={l.href}>{l.text}</a> : l.text}</li>)}
    </ul>
  );
}
